import { call } from '@apparel/core/utils/functions';
import { useLoading } from '@apparel/react/hooks/useLoading';
import { useService } from '@apparel/react/hooks/useService';
import Button, { ButtonProps } from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useCallback } from 'react';
import { GoogleTagService } from '../lib/GoogleTag';
import { GTag } from '../lib/utils';
import { CartIcon } from './CartIcon';
import { CartItem } from './CartItem';
import { CartItemPackage } from './CartResult';
import { CartService } from './CartService';

export interface AddToCartButtonProps extends Omit<ButtonProps, 'size'> {
    loading?: boolean;
    size?: number | string;
    cartItem: CartItem & { package?: Partial<CartItemPackage> };
    buttonColor?: string;
    onAdd?: (item: CartItem) => any;
}

const useStyles = makeStyles(
    (theme) => ({
        root: {
            borderRadius: 2,
        },

        icon: {
            marginRight: 4,
        },

        loader: {
            color: theme.palette.primary.contrastText,
        },
    }),
    {
        name: 'AddToCartButton',
    },
);

export const AddToCartButton = React.memo((props: AddToCartButtonProps) => {
    const { loading, size, cartItem, onAdd, buttonColor, ...rest } = props;

    const cart$ = useService(CartService);
    const classes = useStyles();
    const [requestLoading, requestLoadingActions] = useLoading();

    const handleClick = useCallback(async () => {
        requestLoadingActions.start();
        GoogleTagService.reportConversion(GTag.Add_to_cart);
        if (cartItem.package) {
            await cart$.addPackage({
                ...(cartItem.package || {}),
                quantity: cartItem.quantity ?? 1,
            });
        } else {
            await cart$.addItem(cartItem);
        }
        await call(onAdd, cartItem);
        requestLoadingActions.stop();

        cart$.emitOpenCart();
    }, [onAdd, cartItem]);

    const color = buttonColor ?? 'primary';

    return (
        <Button
            disableElevation
            className={classes.root}
            variant={'contained'}
            color={color as any}
            onClick={handleClick}
            startIcon={
                <CartIcon
                    className={classes.icon}
                    loading={requestLoading || loading}
                    CircularProgressProps={{ classes: { root: classes.loader } }}
                />
            }
            {...rest}
        >
            Add to Cart
        </Button>
    );
});

AddToCartButton.displayName = 'AddToCartButton';

export default AddToCartButton;
